// Header.js -- Admin top bar.
// Visually the same light Jaipuria top bar as the Manager shell
// (MainLayout/Header.js). Only controls that actually work for Admin are
// rendered: sidebar toggle, brand logo, the signed-in user and Logout. The
// Manager bar's search box and notification bell are not wired to anything
// (no handler / hard-coded count), so they are intentionally not copied here.
import React from "react";
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Typography,
  Avatar,
  Button,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import { DRAWER_WIDTH, COLLAPSED_WIDTH } from "./layoutConstants";
import { getAuth } from "../../auth";

const Header = ({ onToggleSidebar, isSidebarOpen }) => {
  const user = getAuth();
  const left = isSidebarOpen ? DRAWER_WIDTH : COLLAPSED_WIDTH;

  // BUG FIX: see the identical fix + full rationale in
  // src/MainLayout/Header.js's own handleLogout -- this only ever removed
  // `"user"`, never `"token"`, from localStorage, letting a still-valid JWT
  // silently re-authenticate the previous session on next load.
  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("token_expires_at");
    window.location.href = "/login";
  };

  const initials = user?.email?.charAt(0)?.toUpperCase() || "U";
  const roleLabel = user?.role
    ? user.role.charAt(0).toUpperCase() + user.role.slice(1)
    : "Administrator";

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        // Above the sidebar drawer (1200) but below Admin popups (1300, see
        // theme/adminModal.css) so a modal's overlay dims this bar too.
        zIndex: 1250,
        background: "rgba(250, 247, 241, 0.94)",
        backdropFilter: "blur(16px)",
        color: "var(--text-primary, #241f1a)",
        borderBottom: "1px solid var(--brand-cream-line, rgba(109, 21, 48, 0.12))",
        boxShadow: "0 1px 2px rgba(74, 14, 34, 0.05)",
        ml: { xs: 0, md: `${left}px` },
        width: { xs: "100%", md: `calc(100% - ${left}px)` },
        transition: "margin-left 220ms ease, width 220ms ease",
      }}
    >
      <Toolbar
        sx={{
          justifyContent: "space-between",
          minHeight: { xs: 62, sm: 68, md: 72 },
          px: { xs: 1, sm: 2, md: 3 },
          whiteSpace: "nowrap",
          gap: { xs: 1, sm: 1.5, md: 2 },
        }}
      >
        {/* Left: toggle + brand */}
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.8, sm: 1.2, md: 1.4 }, minWidth: 0 }}>
          <IconButton
            onClick={onToggleSidebar}
            aria-label="Toggle sidebar"
            sx={{
              width: { xs: 38, md: 42 },
              height: { xs: 38, md: 42 },
              borderRadius: "var(--radius-lg, 12px)",
              color: "var(--brand-maroon, #6d1530)",
              background: "var(--brand-maroon-soft, #fbeef1)",
              border: "1px solid rgba(109, 21, 48, 0.16)",
              flexShrink: 0,
              "&:hover": { background: "rgba(109, 21, 48, 0.16)" },
            }}
          >
            <MenuIcon />
          </IconButton>

          <Box
            component="img"
            src="/assets/brand/jaipuria-it360-logo-trimmed.jpg"
            alt="Jaipuria IT360 Desk"
            sx={{
              height: { xs: 36, sm: 42, md: 48 },
              width: "auto",
              objectFit: "contain",
              flexShrink: 0,
              mixBlendMode: "multiply",
            }}
          />
        </Box>

        {/* Right: user + logout */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: { xs: 0.6, sm: 1, md: 1.2 }, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              minWidth: 0,
              gap: { xs: 0, md: 1.1 },
              pl: { xs: 0, sm: 0.5, md: 0.7 },
              pr: { xs: 0, sm: 0.7, md: 1.2 },
              py: { xs: 0, sm: 0.5, md: 0.6 },
              borderRadius: "var(--radius-lg, 12px)",
              background: { xs: "transparent", sm: "var(--brand-cream, #faf7f1)" },
              border: {
                xs: "1px solid transparent",
                sm: "1px solid var(--brand-cream-line, rgba(109, 21, 48, 0.1))",
              },
            }}
          >
            <Avatar
              sx={{
                width: { xs: 34, md: 38 },
                height: { xs: 34, md: 38 },
                bgcolor: "var(--brand-maroon, #6d1530)",
                color: "#fff",
                fontWeight: 700,
                boxShadow: "0 8px 18px rgba(109, 21, 48, 0.22)",
              }}
            >
              {initials}
            </Avatar>

            <Box sx={{ display: { xs: "none", md: "block" }, minWidth: 0 }}>
              <Typography
                noWrap
                title={user?.email || "Admin User"}
                sx={{
                  fontSize: 13.5,
                  fontWeight: 700,
                  color: "var(--text-primary, #241f1a)",
                  lineHeight: 1.2,
                  // Sized in `ch` so a routine college email address is not
                  // clipped; the title attribute exposes the full address.
                  maxWidth: "30ch",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {user?.email || "Admin User"}
              </Typography>
              <Typography
                sx={{
                  fontSize: 11.5,
                  color: "var(--text-secondary, #6b6259)",
                  fontWeight: 500,
                  lineHeight: 1.2,
                }}
              >
                {roleLabel}
              </Typography>
            </Box>
          </Box>

          {/* Logout stays visible at every width; only the label collapses
              to an icon on narrow screens (same as the Manager bar). */}
          <Button
            onClick={handleLogout}
            aria-label="Logout"
            variant="outlined"
            size="small"
            sx={{
              height: 42,
              minWidth: { xs: 42, md: "auto" },
              width: { xs: 42, md: "auto" },
              px: { xs: 0, md: 2 },
              borderRadius: "var(--radius-lg, 12px)",
              borderColor: "rgba(109, 21, 48, 0.4)",
              color: "var(--brand-maroon, #6d1530)",
              bgcolor: "transparent",
              fontWeight: 700,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                bgcolor: "var(--brand-maroon-soft, #fbeef1)",
                borderColor: "var(--brand-maroon, #6d1530)",
                boxShadow: "none",
              },
              "& .MuiButton-startIcon": { mx: { xs: 0, md: undefined } },
            }}
            startIcon={<LogoutOutlinedIcon sx={{ display: { xs: "none", md: "inline-flex" } }} />}
          >
            <LogoutOutlinedIcon sx={{ display: { xs: "inline-flex", md: "none" }, fontSize: 20 }} />
            <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}>
              Logout
            </Box>
          </Button>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
